import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Search, Layers, UserRound, Send, MessageSquare, AlarmClock, BarChart3, ShieldOff } from 'lucide-react';

import ReachOutTab from './invite/ReachOutTab';
import SearchResultsTab from './invite/SearchResultsTab';
import ProspectsTab from './invite/ProspectsTab';
import InvitedTab from './invite/InvitedTab';
import ConversationsTab from './invite/ConversationsTab';
import FollowupsTab from './invite/FollowupsTab';
import AnalyticsTab from './invite/AnalyticsTab';
import SuppressionTab from './invite/SuppressionTab';
import { SearchProgressProvider, useSearchProgressShared } from '../lib/SearchProgressContext';
import '../lib/inviteForms.css';

const TABS = [
  { key: 'reach-out', label: 'Reach Out', icon: Search, Comp: ReachOutTab },
  { key: 'search-results', label: 'Search Results', icon: Layers, Comp: SearchResultsTab },
  { key: 'prospects', label: 'Prospects', icon: UserRound, Comp: ProspectsTab },
  { key: 'invited', label: 'Invited', icon: Send, Comp: InvitedTab },
  { key: 'conversations', label: 'Conversations', icon: MessageSquare, Comp: ConversationsTab },
  { key: 'followups', label: 'Follow-ups', icon: AlarmClock, Comp: FollowupsTab },
  { key: 'analytics', label: 'Analytics', icon: BarChart3, Comp: AnalyticsTab },
  { key: 'suppression', label: 'Suppression', icon: ShieldOff, Comp: SuppressionTab },
];

export default function InvitePage() {
  return (
    <SearchProgressProvider>
      <InvitePageInner />
    </SearchProgressProvider>
  );
}

function InvitePageInner() {
  const { exhibitionId, selectedExhibition } = useOutletContext();
  const [active, setActive] = useState('reach-out');
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const { snapshot } = useSearchProgressShared();
  const isRunning = snapshot?.status === 'running';

  const ActiveComp = TABS.find((t) => t.key === active)?.Comp || ReachOutTab;

  if (!exhibitionId) {
    return (
      <div className="text-[#777] font-mono text-xs" data-testid="invite-no-exhibition">
        Select an exhibition to use the Invite OS.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invite-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 pb-4 border-b border-[#1a1a1a]">
        <div>
          <p className="text-[10px] font-mono tracking-[0.32em] text-[#90E0EF] uppercase mb-1">Invite OS</p>
          <h1 className="font-outfit text-2xl font-semibold text-white tracking-tight">
            {selectedExhibition?.title || 'Selected exhibition'}
          </h1>
          <p className="text-[11px] font-mono text-[#777] mt-1">
            Discover prospects · dedupe · draft outreach · send · classify replies · convert tickets
          </p>
        </div>
        {isRunning && (
          <div className="flex items-center gap-2 text-[10px] font-mono px-3 py-1.5 border border-[#90E0EF]/40 text-[#90E0EF]" data-testid="invite-running-indicator">
            <span className="block w-1.5 h-1.5 rounded-full bg-[#90E0EF] animate-pulse" style={{ boxShadow: '0 0 8px rgba(144,224,239,0.7)' }} />
            Search running · {snapshot.progress_percent}%
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2" data-testid="invite-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          const showDot = t.key === 'search-results' && isRunning;
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-clickable="true"
              data-testid={`invite-tab-${t.key}`}
              className={`inline-flex items-center gap-2 text-[10px] font-mono px-3 py-1.5 border rounded-none uppercase tracking-[0.14em] transition-all ${
                isActive
                  ? 'border-[#90E0EF] text-[#90E0EF] bg-[#90E0EF]/5 shadow-[0_0_10px_rgba(144,224,239,0.18)]'
                  : 'border-[#222] text-[#777] hover:text-white hover:border-[#333]'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {showDot && <span className="block w-1.5 h-1.5 rounded-full bg-[#90E0EF] animate-pulse" />}
            </button>
          );
        })}
      </div>

      <div>
        <ActiveComp
          exhibitionId={exhibitionId}
          selectedExhibition={selectedExhibition}
          onNavigate={(tab, payload) => {
            setActive(tab);
            if (tab === 'conversations' && payload?.threadId) {
              setSelectedThreadId(payload.threadId);
            }
          }}
          selectedThreadId={selectedThreadId}
          setSelectedThreadId={setSelectedThreadId}
        />
      </div>
    </div>
  );
}
